/**
 * Renderers Module - Generates HTML for all site sections
 */
import { getAbsPath, sanitizeUrlParam, escapeHtml } from './utils.js';
import { computeRelativeTime, computeYesterdayFrom, ensureThreeHeroItems } from './renderers/core.js';
import { renderTicker, renderHero, renderQuickSummary } from './renderers/hero.js';
import { renderResults, renderUpcoming, renderWCNews } from './renderers/sports.js';
import { renderCategories, renderTechnology } from './renderers/categories.js';
import { renderMostRead, renderTopics } from './renderers/trending.js';

export { computeRelativeTime };

/**
 * Main renderer to rule them all. Renders all sections of the homepage.
 * @param {Object} data - The news data object containing all sections.
 * @param {string} [dateQuery=''] - The date parameter if rendering a specific day.
 * @returns {void}
 */
export function renderSmartDashboard(data) {
  // Widget Dólar
  const valDolar = document.getElementById('valDolar');
  const lblDolar = document.getElementById('lblDolar');
  if (data.dolar) {
    if (valDolar) valDolar.innerText = `₲ ${data.dolar.referencial_bcp.toLocaleString('es-PY')}`;
    if (lblDolar) lblDolar.innerText = `Referencial BCP • Compra ₲${data.dolar.compra}`;
  }

  // Widget Clima
  const valClima = document.getElementById('valClima');
  const lblClima = document.getElementById('lblClima');
  const iconClima = document.getElementById('iconClima');
  if (data.clima && data.clima.asuncion) {
    const asu = data.clima.asuncion;
    if (valClima) valClima.innerText = `${asu.temp}°C`;
    if (lblClima) lblClima.innerText = `${asu.desc} • ST ${asu.sensacion}°C`;
    if (iconClima) {
      iconClima.innerHTML = `<i class="fas fa-${asu.icon}"></i>`;
    }
  }

  // Widget Feriado
  const valFeriado = document.getElementById('valFeriado');
  const lblFeriado = document.getElementById('lblFeriado');
  if (data.feriado) {
    if (valFeriado) {
      if (data.feriado.diasFaltantes === 0) {
        valFeriado.innerText = `¡HOY!`;
      } else {
        valFeriado.innerText = `${data.feriado.diasFaltantes} ${data.feriado.diasFaltantes === 1 ? 'día' : 'días'}`;
      }
    }
    if (lblFeriado) lblFeriado.innerText = `${data.feriado.nombre}`;
  }

  // Widget Albirroja
  const valAlbirroja = document.getElementById('valAlbirroja');
  const lblAlbirroja = document.getElementById('lblAlbirroja');
  if (data.albirroja) {
    if (valAlbirroja) {
      if (data.albirroja.sinPartido) {
        valAlbirroja.innerText = data.albirroja.statusText || 'Por confirmar';
      } else if (typeof data.albirroja.diasFaltantes === 'number' && data.albirroja.diasFaltantes !== null) {
        if (data.albirroja.diasFaltantes === 0) {
          valAlbirroja.innerText = `¡HOY!`;
        } else {
          valAlbirroja.innerText = `${data.albirroja.diasFaltantes} ${data.albirroja.diasFaltantes === 1 ? 'día' : 'días'}`;
        }
      } else {
        valAlbirroja.innerText = data.albirroja.statusText || 'Pendiente';
      }
    }
    if (lblAlbirroja) {
      const rival = data.albirroja.rival || '';
      if (!data.albirroja.sinPartido && rival && rival.toLowerCase() !== 'por confirmar' && rival.toLowerCase() !== 'por definir') {
        lblAlbirroja.innerText = `vs ${rival}`;
      } else {
        lblAlbirroja.innerText = data.albirroja.rivalSubtext || 'Próximo partido por confirmar';
      }
    }
  }
}

export function renderAll(data, dateQuery = '') {
  // Render Smart Dashboard widgets first
  renderSmartDashboard(data);

  const dateSuffix = (dateQuery && dateQuery !== 'today') ? `&date=${sanitizeUrlParam(dateQuery)}` : '';
  const editionDateStr = data.editionDate || null;
  const resultsFallbackDate = computeYesterdayFrom(editionDateStr);
  const fullHero = ensureThreeHeroItems(data.hero, data.categories);

  const sections = [
    { id: 'breakingTicker', fn: renderTicker, arg: data.breaking },
    { id: 'newsHeroGrid', fn: renderHero, arg: fullHero, extra: dateSuffix },
    { id: 'quickSummaryList', fn: renderQuickSummary, arg: data.quickSummary },
    { id: 'categoriesGrid', fn: renderCategories, arg: data.categories, extra: dateSuffix },
    { id: 'yesterdayResultsGrid', fn: renderResults, arg: data.sportsScoreboard?.yesterdayResults, extra: resultsFallbackDate, showFlags: false },
    { id: 'upcomingMatchesGrid', fn: renderUpcoming, arg: data.sportsScoreboard?.upcomingMatches, extra: false },
    { id: 'wcMatchesGrid', fn: renderUpcoming, arg: data.sportsScoreboard?.worldCupMatches, extra: true },
    { id: 'wcNewsGrid', fn: renderWCNews, arg: data.worldCupNews, extra: dateSuffix },
    { id: 'technologyGrid', fn: renderTechnology, arg: data.technology, extra: dateSuffix },
    { id: 'trendingMostRead', fn: renderMostRead, arg: data.trending?.mostRead },
    { id: 'topicsCloud', fn: renderTopics, arg: data.trending?.topics }
  ];

  sections.forEach(section => {
    const container = document.getElementById(section.id);
    if (!container) return;

    let sectionData = section.arg;

    if (section.id === 'yesterdayResultsGrid' || section.id === 'upcomingMatchesGrid') {
      if (!sectionData && data.sportsScoreboard?.matches) {
        const matches = data.sportsScoreboard.matches;
        if (section.id === 'yesterdayResultsGrid') {
          sectionData = matches.filter(m => m.status === 'Final' || m.score.includes('-'));
        } else {
          sectionData = matches.filter(m => m.status !== 'Final' && !m.score.includes('-'));
        }
      }
    }

    if (sectionData && sectionData.length > 0) {
      // Pass all potential arguments to the renderer function
      container.innerHTML = section.fn(sectionData, section.extra, section.showFlags);
    } else {
      container.innerHTML = '';
    }
  });

  const parentSections = new Set();
  sections.forEach(s => {
    const el = document.getElementById(s.id);
    if (el) {
      const parent = el.closest('section');
      if (parent) parentSections.add(parent);
    }
  });

  parentSections.forEach(section => {
    const trackedContainers = sections
      .filter(s => {
        const el = document.getElementById(s.id);
        return el && el.closest('section') === section;
      })
      .map(s => document.getElementById(s.id));

    const hasAnyContent = trackedContainers.some(c => c.innerHTML.trim() !== '');
    
    if (section.id === 'tendencias') {
       section.style.display = '';
       return;
    }

    if (hasAnyContent) {
      section.style.display = '';
    } else {
      section.style.display = 'none';
    }
  });

  const titleEl = document.getElementById('recentResultsTitle');
  if (titleEl && resultsFallbackDate) {
    titleEl.innerHTML = `<i class="fas fa-check-double"></i> Resultados Recientes <small style="font-size:0.7em;font-weight:500;opacity:0.6;">(${resultsFallbackDate})</small>`;
  }
}

/**
 * Render only above-the-fold content (Hero, Ticker, Scoreboard) to improve first paint/LCP.
 * @param {Object} data - The critical news data object.
 * @param {string} [dateQuery=''] - The date parameter.
 * @returns {void}
 */
export function renderCritical(data, dateQuery = '') {
  // Render Smart Dashboard widgets from critical data
  renderSmartDashboard(data);

  const dateSuffix = (dateQuery && dateQuery !== 'today') ? `&date=${sanitizeUrlParam(dateQuery)}` : '';
  const heroContainer = document.getElementById('newsHeroGrid');
  const tickerContainer = document.getElementById('breakingTicker');

  if (tickerContainer && data.breaking) {
    tickerContainer.innerHTML = renderTicker(data.breaking);
  }

  if (heroContainer && data.hero) {
    const fullHero = ensureThreeHeroItems(data.hero, data.categories || []);
    heroContainer.innerHTML = renderHero(fullHero, dateSuffix, { animate: false });
  }

  if (data.sportsScoreboard) {
    const yesterdayGrid = document.getElementById('yesterdayResultsGrid');
    const upcomingGrid = document.getElementById('upcomingMatchesGrid');
    const resultsFallbackDate = computeYesterdayFrom(data.editionDate || null);

    if (yesterdayGrid && data.sportsScoreboard.yesterdayResults) {
      yesterdayGrid.innerHTML = renderResults(data.sportsScoreboard.yesterdayResults, resultsFallbackDate, false);
    }
    if (upcomingGrid && data.sportsScoreboard.upcomingMatches) {
      upcomingGrid.innerHTML = renderUpcoming(data.sportsScoreboard.upcomingMatches, false);
    }

    const scoreboardSection = document.getElementById('marcadorDeportivo');
    if (scoreboardSection && (data.sportsScoreboard.yesterdayResults?.length > 0 || data.sportsScoreboard.upcomingMatches?.length > 0)) {
      scoreboardSection.style.display = '';
    }
  }
}

/**
 * Render only below-the-fold and non-critical homepage sections.
 * @param {Object} data - The complete news data object.
 * @returns {void}
 */
export function renderDeferred(data) {
  // Render Smart Dashboard widgets
  renderSmartDashboard(data);

  const sections = [
    { id: 'breakingTicker', fn: renderTicker, arg: data.breaking },
    { id: 'newsHeroGrid', fn: renderHero, arg: ensureThreeHeroItems(data.hero, data.categories), extra: '' },
    { id: 'quickSummaryList', fn: renderQuickSummary, arg: data.quickSummary },
    { id: 'categoriesGrid', fn: renderCategories, arg: data.categories },
    { id: 'yesterdayResultsGrid', fn: renderResults, arg: data.sportsScoreboard?.yesterdayResults, extra: computeYesterdayFrom(data.editionDate || null), showFlags: false },
    { id: 'upcomingMatchesGrid', fn: renderUpcoming, arg: data.sportsScoreboard?.upcomingMatches, extra: false },
    { id: 'wcMatchesGrid', fn: renderUpcoming, arg: data.sportsScoreboard?.worldCupMatches, extra: true },
    { id: 'wcNewsGrid', fn: renderWCNews, arg: data.worldCupNews },
    { id: 'technologyGrid', fn: renderTechnology, arg: data.technology },
    { id: 'trendingMostRead', fn: renderMostRead, arg: data.trending?.mostRead },
    { id: 'topicsCloud', fn: renderTopics, arg: data.trending?.topics }
  ];

  sections.forEach(section => {
    const container = document.getElementById(section.id);
    if (!container) return;
    
    const sectionData = section.arg;
    if (sectionData && sectionData.length > 0) {
      if (section.fn === renderResults) {
        container.innerHTML = section.fn(sectionData, section.extra, section.showFlags);
      } else {
        container.innerHTML = section.fn(sectionData, section.extra);
      }
    } else {
      container.innerHTML = '';
    }
  });

  const parentSections = new Set();
  sections.forEach(s => {
    const el = document.getElementById(s.id);
    if (el) {
      const parent = el.closest('section');
      if (parent) parentSections.add(parent);
    }
  });

  parentSections.forEach(section => {
    const trackedContainers = sections
      .filter(s => {
        const el = document.getElementById(s.id);
        return el && el.closest('section') === section;
      })
      .map(s => document.getElementById(s.id));

    const hasAnyContent = trackedContainers.some(c => c.innerHTML.trim() !== '');
    
    if (section.id === 'tendencias') {
       section.style.display = '';
       return;
    }

    if (hasAnyContent) {
      section.style.display = '';
    } else {
      section.style.display = 'none';
    }
  });

  const titleEl = document.getElementById('recentResultsTitle');
  const resultsFallbackDate = computeYesterdayFrom(data.editionDate || null);
  if (titleEl && resultsFallbackDate) {
    titleEl.innerHTML = `<i class="fas fa-check-double"></i> Resultados Recientes <small style="font-size:0.7em;font-weight:500;opacity:0.6;">(${resultsFallbackDate})</small>`;
  }
}

/**
 * Show skeleton loaders in main containers while data is being fetched.
 * @returns {void}
 */
export function showSkeletons() {
  const heroContainer = document.getElementById('newsHeroGrid');
  if (heroContainer) {
    heroContainer.innerHTML = `
      <div class="skeleton" style="grid-column: 1 / -1; height: 520px; border-radius: 16px;"></div>
      <div class="skeleton" style="height: 280px; border-radius: 16px;"></div>
      <div class="skeleton" style="height: 280px; border-radius: 16px;"></div>
    `;
  }
  
  const quickList = document.getElementById('quickSummaryList');
  if (quickList) {
    quickList.innerHTML = Array(5).fill(0).map(() => `
      <div class="skeleton" style="height: 100px; margin-bottom: 1rem; border-radius: 12px;"></div>
    `).join('');
  }
}
